import {
  Box,
  ProgressBarDetail,
  ProgressBarDetailSpacing,
  ProgressBarSize,
  Set,
  SetDirection,
  Text,
} from '@bechtel/gds';
import { OwnershipType, WorkPackage } from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { capitalize, map, slice } from 'lodash';
import React, { Fragment } from 'react';
import { TextWithEllipsis } from '~/components/atoms/text-with-ellipsis';
import { useWorkPackageOwner } from '~/hooks';
import { useDateRange } from '~/hooks/use-date-range';
import { OwnerShipTypeBullet, WorkpackageItemCardStyled } from './styled';

interface CommodityCountTagProps {
  commodityTypeName: string;
  totalQuantity: number;
  // numOnSite: number;
}
const CommodityCountTag: React.FC<CommodityCountTagProps> = ({ commodityTypeName, totalQuantity }) => {
  return (
    <Fragment>
      <Text size="250" color="grey.1000">
        {/* TODO: implement numOnSite value once DSC data is available */}
        {/* {commodityTypeName} : <Text color="grey.600">{numOnSite}/{totalQuantity}</Text> */}
        {commodityTypeName} : <Text color="grey.600">--/{totalQuantity}</Text>
      </Text>
    </Fragment>
  );
};

interface WorkPackageItemCardProps {
  workPackage: WorkPackage;
  showOwnershipType: boolean; // this value should be false for planning as secondary owners won't see the WPs
}

export const WorkPackageItemCard: React.FC<WorkPackageItemCardProps> = ({ workPackage, showOwnershipType }) => {
  const [dateRangeLabel] = useDateRange(workPackage.startDate, workPackage.endDate);
  const percentageComplete = Math.round((workPackage.percentageComplete ?? 0) * 100) / 100;
  const { i18n } = useLingui();

  const isPrimaryOwner = useWorkPackageOwner(workPackage.primaryOwners);
  const isSecondaryOwner = !isPrimaryOwner
    ? useWorkPackageOwner([...workPackage.secondaryOwners, ...workPackage.otherOwners])
    : false;

  const ownershipType: OwnershipType | null = isPrimaryOwner
    ? OwnershipType.PRIMARY
    : isSecondaryOwner
      ? OwnershipType.SECONDARY
      : null;

  return (
    <WorkpackageItemCardStyled>
      <Box mb={{ sm: '300' }} data-testid={`workpackage-card`}>
        <TextWithEllipsis
          family="graphik"
          size="400"
          color={workPackage.numBlocks > 0 ? 'red.700' : 'grey.1000'}
          weight="600"
          transform="uppercase"
        >
          {workPackage.tag}
        </TextWithEllipsis>
      </Box>
      <Box mb={{ sm: '500' }} data-testid={`workpackage-card-date-range`}>
        <Text size="250" color="grey.600">
          {i18n._(dateRangeLabel)}
        </Text>
        {showOwnershipType && ownershipType && (
          <Box>
            <OwnerShipTypeBullet />
            <Text size="250" color="grey.600">
              <Trans>{capitalize(ownershipType.toString())}</Trans>
            </Text>
          </Box>
        )}
      </Box>
      <Set direction={SetDirection.ROW} style={{ flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
        {map(slice(workPackage.materialsOnSite, 0, 3), (materialOnsite) => {
          return (
            <CommodityCountTag
              key={materialOnsite.commodityType.id}
              commodityTypeName={materialOnsite.commodityType.name}
              totalQuantity={materialOnsite.totalCommodities}
              // numOnSite={materialOnsite.numOnSite}
            />
          );
        })}
      </Set>

      <ProgressBarDetail
        size={ProgressBarSize.SMALL}
        currentValue={workPackage.earnedHours}
        totalValue={workPackage.totalEarnableHours}
        usePercentage={true}
        percentage={percentageComplete}
        fillPattern={!!workPackage.numBlocks}
        spacing={ProgressBarDetailSpacing.XSMALL}
        leftLabel={() => {
          return (
            <Text as="label" color="grey.600" size="200">
              {workPackage.numBlocks > 0 ? `${workPackage.numBlocks} ${t`Blocked Milestones`}` : t`In Progress`}
            </Text>
          );
        }}
        rightLabel={() => {
          return (
            <Text as="label" color="grey.600" size="200">
              {`${percentageComplete} ${t`% Complete`}`}
            </Text>
          );
        }}
        bgColor="white"
        borderColor="grey.300"
      />
    </WorkpackageItemCardStyled>
  );
};
